/**
 * Every public download surface for Gab AI — store URLs, labels, and the copy
 * the `/download` page renders. Store links live in `publicFooterStoreLinks`;
 * this module adds the marketing metadata so badges, nav, and SEO stay aligned.
 */

import AppStoreBadge from '@/components/Icons/AppStoreBadge';
import GooglePlayBadge from '@/components/Icons/GooglePlayBadge';
import MacAppStoreBadge from '@/components/Icons/MacAppStoreBadge';
import {
  APP_STORE_URL,
  GOOGLE_PLAY_URL,
} from '@/components/PublicFooter/publicFooterStoreLinks';

/** Hub route for the Download nav item. */
export const DOWNLOAD_NAV_PATH = '/download';

export const DOWNLOAD_PLATFORMS = [
  {
    key: 'ios',
    kicker: 'Mobile',
    name: 'iPhone & iPad',
    description: 'The full workspace in your pocket — chat, voice, images, video, music, and file uploads with the same models you use on the web.',
    icon: 'mobile-screen',
    url: APP_STORE_URL,
    external: true,
    badge: AppStoreBadge,
    badgeLabel: 'Download on the App Store',
    badgeWidth: 152,
    requirements: 'iOS 16+ · iPadOS 16+',
    highlights: ['Voice mode and push alerts', 'Camera and photo imports', 'Same credits and memory'],
  },
  {
    key: 'android',
    kicker: 'Mobile',
    name: 'Android',
    description: 'Native Android app with the same chat history, agents, and generators. Install from Google Play and sign in with the account you already have.',
    icon: 'mobile-screen-button',
    url: GOOGLE_PLAY_URL,
    external: true,
    badge: GooglePlayBadge,
    badgeLabel: 'Get it on Google Play',
    badgeWidth: 168,
    requirements: 'Android 10+',
    highlights: ['Google Play billing for Plus', 'Share-to-Gab from any app', 'Offline-friendly drafts'],
  },
  {
    key: 'mac',
    kicker: 'Desktop',
    name: 'macOS',
    description: 'A Mac app that keeps Gab AI beside your editor and browser. Keyboard-first chat, file drag-and-drop, and the Mac App Store install flow you expect.',
    icon: 'desktop',
    url: APP_STORE_URL,
    external: true,
    badge: MacAppStoreBadge,
    badgeLabel: 'Download on the Mac App Store',
    badgeWidth: 188,
    requirements: 'macOS 13+ · Apple Silicon & Intel',
    highlights: ['Menu bar quick access', 'Drag files straight into chat', 'Native window management'],
  },
  {
    key: 'web',
    kicker: 'Everywhere',
    name: 'Web',
    description: 'No install required. Open gab.ai in Chrome, Safari, Firefox, or Edge on Windows, Linux, Chromebook, or any machine with a browser.',
    icon: 'globe',
    url: '/signup',
    external: false,
    badge: null,
    badgeLabel: 'Start free on the web',
    requirements: 'Any modern browser',
    highlights: ['Nothing to install', 'Works on Windows & Linux', 'Full feature surface'],
  },
];

/** Capability chips for the hero marquee — mediums, not store brands. */
export const DOWNLOAD_MARQUEE = [
  'iPhone', 'iPad', 'Android', 'Mac', 'Web', 'Sync', 'Voice', 'Chat',
  'Images', 'Video', 'Music', 'Files', 'Agents', 'Tasks', 'Memory',
];

/** Cross-device promises for the sync band. */
export const DOWNLOAD_SYNC_POINTS = [
  { icon: 'comments', title: 'One conversation thread', detail: 'Pick up a chat on your phone where you left it on your Mac.' },
  { icon: 'brain', title: 'Shared memory', detail: 'Preferences and facts you teach the AI follow you across devices.' },
  { icon: 'coins', title: 'One credit balance', detail: 'Images on your laptop and voice on your phone draw on the same pool.' },
  { icon: 'folder-open', title: 'Files and projects', detail: 'Uploads, decks, and documents stay in the library you already have.' },
];
